import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const DEFAULT_BOUNDS = { width: 900, height: 600, left: null, top: null };
const WINDOW_FEATURES = ["location=no", "menubar=no", "toolbar=no", "status=no"];
const POLL_INTERVAL_MS = 500;
const PORTAL_ROOT_ID = "portal-root";

/**
 * Hook that creates a React portal to a new browser window.
 * Maintains full React context (state, stores, actions) in the new window.
 *
 * @param {Object} options - Configuration options
 * @param {string} options.title - Window title
 * @param {string} [options.storageKey] - localStorage key for persisting window bounds
 * @param {string} [options.bodyClassName="si-portal-body"] - CSS class for the popup body element
 * @param {number} [options.defaultWidth=900] - Default window width
 * @param {number} [options.defaultHeight=600] - Default window height
 * @param {Function} [options.onClose] - Callback when window is closed
 * @returns {Object} Portal component and control functions
 */
const useWindowPortal = ({
  title,
  storageKey,
  bodyClassName = "si-portal-body",
  defaultWidth = 900,
  defaultHeight = 600,
  onClose,
}) => {
  const [container, setContainer] = useState(null);
  const windowRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const parentUnloadHandlerRef = useRef(null);

  /**
   * Get stored window state from localStorage
   * @returns {{ width: number, height: number, left: number|null, top: number|null, windowName: string|null } | null}
   */
  const getStoredWindowState = useCallback(() => {
    if (!storageKey) return null;
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  }, [storageKey]);

  // Close any orphaned windows from previous sessions (e.g., after page refresh)
  useEffect(() => {
    const state = getStoredWindowState();
    if (state?.windowName) {
      try {
        // Try to get reference to existing window and close it
        const orphan = window.open("", state.windowName);
        if (orphan && !orphan.closed) {
          orphan.close();
        }
      } catch {
        // Ignore errors - window may not exist or be inaccessible
      }
    }
  }, [getStoredWindowState]);

  /**
   * Load saved window bounds from localStorage
   */
  const getSavedBounds = useCallback(() => {
    const state = getStoredWindowState();
    if (state) {
      return { ...DEFAULT_BOUNDS, ...state };
    }
    return { width: defaultWidth, height: defaultHeight };
  }, [getStoredWindowState, defaultWidth, defaultHeight]);

  /**
   * Save current window bounds and windowName to localStorage
   * @param {Window} win - The popup window
   * @param {string|null} windowName - Window name for orphan detection (null to clear)
   */
  const saveBounds = useCallback(
    (win, windowName) => {
      if (!storageKey || !win) return;
      try {
        const bounds = {
          width: win.outerWidth,
          height: win.outerHeight,
          left: win.screenX,
          top: win.screenY,
          windowName,
        };
        localStorage.setItem(storageKey, JSON.stringify(bounds));
      } catch (e) {
        console.warn("Failed to save window bounds:", e);
      }
    },
    [storageKey],
  );

  /**
   * Copy all stylesheets from source document to target document
   */
  const copyStyles = useCallback((sourceDoc, targetDoc) => {
    // Copy <link> stylesheets
    Array.from(sourceDoc.querySelectorAll('link[rel="stylesheet"]')).forEach((link) => {
      const newLink = targetDoc.createElement("link");
      newLink.rel = "stylesheet";
      newLink.href = link.href;
      targetDoc.head.appendChild(newLink);
    });

    // Copy <style> elements
    Array.from(sourceDoc.querySelectorAll("style")).forEach((style) => {
      const newStyle = targetDoc.createElement("style");
      newStyle.textContent = style.textContent;
      targetDoc.head.appendChild(newStyle);
    });
  }, []);

  /**
   * Open the portal window
   * @returns {boolean} True if window opened successfully, false if blocked
   */
  const openWindow = useCallback(() => {
    // If already open, just focus
    if (windowRef.current && !windowRef.current.closed) {
      windowRef.current.focus();
      return true;
    }

    const bounds = getSavedBounds();
    const features = [
      `width=${bounds.width}`,
      `height=${bounds.height}`,
      bounds.left !== null ? `left=${bounds.left}` : "",
      bounds.top !== null ? `top=${bounds.top}` : "",
      ...WINDOW_FEATURES,
    ]
      .filter(Boolean)
      .join(",");

    // Generate unique window name for orphan cleanup
    // Use storageKey as unique identifier, fallback to sanitized title
    const windowPrefix = storageKey || title.replace(/\s+/g, "_");
    const windowName = `${windowPrefix}_${Date.now()}`;

    const win = window.open("", windowName, features);
    if (!win) {
      console.warn("Popup blocked - please allow popups for this site");
      return false;
    }

    // Move window to saved position (more reliable than feature string for multi-monitor)
    if (bounds.left !== null && bounds.top !== null) {
      win.moveTo(bounds.left, bounds.top);
    }

    win.document.title = title;

    // Copy dark/light mode class from parent body
    const isDarkMode = document.body.classList.contains("dark");
    win.document.body.className = `${bodyClassName} ${isDarkMode ? "dark" : "light"}`;

    const containerDiv = win.document.createElement("div");
    containerDiv.id = PORTAL_ROOT_ID;
    win.document.body.appendChild(containerDiv);

    copyStyles(document, win.document);

    windowRef.current = win;
    setContainer(containerDiv);

    // Heartbeat: popup self-closes if opener (parent window) is gone
    const heartbeatInterval = setInterval(() => {
      if (!win.opener || win.opener.closed) {
        clearInterval(heartbeatInterval);
        win.close();
      }
    }, 1000);

    // Save bounds before close (pass windowName explicitly for orphan detection on refresh)
    win.addEventListener("beforeunload", () => {
      clearInterval(heartbeatInterval);
      saveBounds(win, windowName);
      onClose?.();
    });

    // Close popup when parent window unloads (refresh/close)
    parentUnloadHandlerRef.current = () => {
      if (win && !win.closed) {
        saveBounds(win, windowName); // Save bounds before closing (beforeunload may not fire)
        win.close();
      }
    };
    window.addEventListener("beforeunload", parentUnloadHandlerRef.current);

    // Backup: poll to detect closed window (some browsers don't fire beforeunload reliably)
    checkIntervalRef.current = setInterval(() => {
      if (win.closed) {
        clearInterval(checkIntervalRef.current);
        setContainer(null);
        onClose?.();
      }
    }, POLL_INTERVAL_MS);

    return true;
  }, [title, storageKey, bodyClassName, getSavedBounds, saveBounds, copyStyles, onClose]);

  /**
   * Close the portal window
   */
  const closeWindow = useCallback(() => {
    if (windowRef.current) {
      // Save bounds but clear windowName since this is intentional close
      saveBounds(windowRef.current, null);
      windowRef.current.close();
    }
    windowRef.current = null;
    setContainer(null);
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
    if (parentUnloadHandlerRef.current) {
      window.removeEventListener("beforeunload", parentUnloadHandlerRef.current);
      parentUnloadHandlerRef.current = null;
    }
  }, [saveBounds]);

  /**
   * Focus the portal window if open
   */
  const focusWindow = useCallback(() => {
    if (windowRef.current && !windowRef.current.closed) {
      windowRef.current.focus();
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (windowRef.current) {
        // Save bounds but clear windowName since this is intentional close
        saveBounds(windowRef.current, null);
        windowRef.current.close();
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (parentUnloadHandlerRef.current) {
        window.removeEventListener("beforeunload", parentUnloadHandlerRef.current);
      }
    };
  }, [saveBounds]);

  // Sync dark/light mode class from parent to portal window
  useEffect(() => {
    if (!container) return;

    const syncTheme = () => {
      const win = windowRef.current;
      if (!win || win.closed) return;

      const isDarkMode = document.body.classList.contains("dark");
      win.document.body.classList.remove("dark", "light");
      win.document.body.classList.add(isDarkMode ? "dark" : "light");
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "class") {
          syncTheme();
          break;
        }
      }
    });

    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, [container]);

  /**
   * Portal component that renders children into the external window
   */
  const PortalWindow = ({ children }) => {
    if (!container) return null;
    return createPortal(children, container);
  };

  return {
    PortalWindow,
    isOpen: !!container,
    openWindow,
    closeWindow,
    focusWindow,
    windowRef,
  };
};

export default useWindowPortal;
